// Unpublished Work © 2023-2024 Deere & Company.

import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

interface OtherDealerInventoryHeaderProps {
  distanceLimit: number;
  setDistanceLimit: (value: number) => void;
  distanceUnit: string;
  setDistanceUnit: (value: string) => void;
}

const OtherDealerInventoryHeader = ({
  distanceLimit,
  setDistanceLimit,
  distanceUnit,
  setDistanceUnit,
}: OtherDealerInventoryHeaderProps) => {
  const theme = useTheme();
  const { t } = useTranslation(["d2d", "common"]);

  return (
    <Box
      sx={{
        marginBottom: "1.5rem",
      }}
    >
      <Typography
        component="h1"
        data-fuel-size="3xl"
        data-testid="other-dealer-inventory-header"
      >
        {t("d2d:otherDealerInventory.header.title", "Other Dealer Inventory")}
      </Typography>

      <Typography
        component="h3"
        sx={{
          fontWeight: theme.tokens.FuelFontWeightSemibold,
          fontSize: theme.tokens.FuelFontSizeHeading3,
          marginTop: theme.tokens.FuelSpacing2,
          lineHeight: theme.tokens.FuelLineHeightLg,
          color: theme.tokens.FuelColorTextSecondary,
        }}
        data-testid="inventory-subtitle"
      >
        {t(
          "d2d:otherDealerInventory.subHeader",
          "Dealer Part List By Geographical Location"
        )}
      </Typography>
      {/* Add distance controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginTop: theme.tokens.FuelSpacing4,
          gap: theme.tokens.FuelSpacing4,
        }}
      >
        <TextField
          label={t("d2d:otherDealerInventory.distanceLimit", "Distance")}
          type="number"
          value={distanceLimit === 0 ? "" : distanceLimit}
          onChange={(e) => {
            const { value } = e.target as HTMLInputElement; // correct extraction of value
            if (value === "" || value === "0") {
              setDistanceLimit(0);
            } else {
              const numValue = parseInt(value, 10);
              if (!Number.isNaN(numValue) && numValue > 0) {
                setDistanceLimit(numValue);
              }
            }
          }}
          onKeyDown={(e) => {
            // If the field has 0 and user starts typing a number, clear it first
            if (distanceLimit === 0 && /[0-9]/.test(e.key)) {
              setDistanceLimit(0);
              // Allow the new digit to be entered
            }
          }}
          placeholder="Enter distance"
          inputProps={{ min: 1 }}
          size="small"
          sx={{ width: 120 }}
          data-testid="distance-limit-input"
        />

        <FormControl size="small" sx={{ width: 150 }}>
          <InputLabel>
            {t("d2d:otherDealerInventory.distanceUnit", "Unit")}
          </InputLabel>
          <Select
            value={distanceUnit}
            onChange={(e) => setDistanceUnit(e.target.value as string)}
            label={t("d2d:otherDealerInventory.distanceUnit", "Unit")}
            data-testid="distance-unit-select"
          >
            <MenuItem value="MI">
              {t("d2d:otherDealerInventory.miles", "Miles")}
            </MenuItem>
            <MenuItem value="KM">
              {t("d2d:otherDealerInventory.kilometers", "Kilometers")}
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default OtherDealerInventoryHeader;
